/**
 * Thin typed fetch wrapper for the istari-server REST API (Wave 2 contract,
 * see ./types.ts). Every response body passes a runtime guard from ./guards.ts
 * before it reaches application code.
 */
import {
  guardAirflows,
  guardAutoAssign,
  guardCircuits,
  guardDevices,
  guardDiff,
  guardDucts,
  guardEquipment,
  guardFindings,
  guardFittings,
  guardGenerateAccepted,
  guardMepSystem,
  guardMepSystems,
  guardModelCreated,
  guardProject,
  guardProjects,
  guardReadiness,
  guardReviewItem,
  guardReviewItems,
  guardScrapeRun,
  guardScraperSource,
  guardScraperSources,
  guardSpace,
  guardSpaces,
  guardStoreys,
  guardTerminals,
  guardZones,
} from "./guards";
import type {
  ApiFinding,
  ApiProject,
  ApiSpace,
  ApiStorey,
  AutoAssignOptions,
  AutoAssignResult,
  ElectricalOutput,
  GenerateAccepted,
  GenerateRequest,
  MepAirflow,
  MepCircuit,
  MepDevice,
  MepDuct,
  MepEquipment,
  MepFitting,
  MepSystem,
  MepTerminal,
  MepZone,
  MechanicalOutput,
  ModelCreated,
  ModelDiff,
  ReadinessReport,
  ReviewItem,
  ReviewItemPatch,
  ScrapeRunAccepted,
  ScraperSource,
  ScraperSourceCreate,
  SpacePatch,
} from "./types";
import type { ExtractionEnvelopeV1, ModelSummary } from "../viewer/ifc/types";
import type { LayerBeam, LayerColumn, LayerLateral } from "../mep/members";

/** Non-2xx HTTP response from the API. */
export class ApiError extends Error {
  readonly status: number;
  /** Response body text (may be empty or JSON). */
  readonly body: string;

  constructor(method: string, path: string, status: number, body: string) {
    super(`${method} ${path} failed with HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request(
  method: string,
  path: string,
  init: Omit<RequestInit, "method"> = {},
): Promise<Response> {
  const res = await fetch(path, { ...init, method });
  if (!res.ok) {
    let body = "";
    try {
      body = await res.text();
    } catch {
      // Body unreadable — keep the status-only error.
    }
    throw new ApiError(method, path, res.status, body);
  }
  return res;
}

async function requestJson(
  method: string,
  path: string,
  init: Omit<RequestInit, "method"> = {},
): Promise<unknown> {
  const res = await request(method, path, init);
  return res.json() as Promise<unknown>;
}

const jsonBody = (value: unknown): Omit<RequestInit, "method"> => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(value),
});

export async function listProjects(): Promise<ApiProject[]> {
  return guardProjects(await requestJson("GET", "/api/projects"));
}

/**
 * One subtype declaration scoped to a ruleset (ENG-14678 / ADR 0011).
 *
 * Returned by `GET /api/vocab/occupancy?ruleset=<id>` — the wizard's
 * secondary picker reads these per occupancy.
 */
export interface OccupancySubtype {
  /** Stable subtype id, scoped to the ruleset (e.g. `aii`). */
  id: string;
  /** Human-readable label for UI display. */
  label: string;
  /** Citation back to the source standard, when declared. */
  citation: string | null;
}

/**
 * One occupancy vocabulary entry from `GET /api/vocab/occupancy`.
 *
 * The canonical vocabulary lives in the Rust enum
 * `istari_core::occupancy::OccupancyCategory`. Fetching at runtime (instead of
 * mirroring it in TS) keeps the wizard in sync with the running server.
 *
 * `subtypes` is empty unless the request was made with `?ruleset=<id>`.
 */
export interface OccupancyVocabEntry {
  /** Stable wire key (snake_case). */
  key: string;
  /** Human-readable label (Title Case). */
  label: string;
  /**
   * Subtypes the requested ruleset declares for this occupancy. Empty when
   * no ruleset was requested or the ruleset doesn't refine this occupancy.
   */
  subtypes: OccupancySubtype[];
}

// Cache key: empty string for "no ruleset", else the ruleset id.
const occupancyVocabPromisesByRuleset: Map<string, Promise<OccupancyVocabEntry[]>> = new Map();

/**
 * Fetch (and cache, per ruleset) the canonical occupancy vocabulary.
 * Repeated calls with the same `rulesetId` share a single promise; different
 * rulesets get separate cache entries (so the wizard can switch rulesets
 * without re-fetching the no-ruleset list).
 */
export async function getOccupancyVocab(rulesetId?: string): Promise<OccupancyVocabEntry[]> {
  const key = rulesetId ?? "";
  const cached = occupancyVocabPromisesByRuleset.get(key);
  if (cached) return cached;
  const promise = (async () => {
    const url = rulesetId
      ? `/api/vocab/occupancy?ruleset=${encodeURIComponent(rulesetId)}`
      : `/api/vocab/occupancy`;
    const body = (await requestJson("GET", url)) as {
      items: OccupancyVocabEntry[];
    };
    return body.items;
  })().catch((err) => {
    // Drop the failed promise so a retry can attempt a fresh fetch.
    occupancyVocabPromisesByRuleset.delete(key);
    throw err;
  });
  occupancyVocabPromisesByRuleset.set(key, promise);
  return promise;
}

/** Test-only: drop the cached promises so tests can mock fresh fetches. */
export function _resetOccupancyVocabCache(): void {
  occupancyVocabPromisesByRuleset.clear();
}

export async function createProject(name: string): Promise<ApiProject> {
  return guardProject(await requestJson("POST", "/api/projects", jsonBody({ name })));
}

/** Upload the raw .ifc as multipart; returns the new model id. */
export async function uploadModel(projectId: string, file: File): Promise<ModelCreated> {
  const form = new FormData();
  form.append("file", file, file.name);
  return guardModelCreated(
    await requestJson("POST", `/api/projects/${encodeURIComponent(projectId)}/models`, {
      body: form,
    }),
  );
}

/** PUT the worker-extracted ModelSummary (verbatim type) for a model. */
export async function putModelSummary(modelId: string, summary: ModelSummary): Promise<void> {
  await request("PUT", `/api/models/${encodeURIComponent(modelId)}/summary`, jsonBody(summary));
}

/** PUT the Fragments binary for a model. */
export async function putModelFragments(modelId: string, fragments: ArrayBuffer): Promise<void> {
  await request("PUT", `/api/models/${encodeURIComponent(modelId)}/fragments`, {
    headers: { "Content-Type": "application/octet-stream" },
    body: fragments,
  });
}

/**
 * PUT the full evidence-bearing envelope (`{version, summary, evidence}`) for
 * a model. Unlike `putModelSummary` (which forwards only the `ModelSummary`
 * to the legacy, evidence-blind `/summary` route), this hits
 * `/api/models/{id}/ingestion` — the route that also persists the
 * `ExtractionEvidenceV1` and runs `evaluate_quality` against it, so the
 * model gets a durable quality verdict instead of staying on the
 * legacy/pending quality path (ENG-17381 Revit IFC readiness).
 */
export async function putModelIngestion(
  modelId: string,
  envelope: ExtractionEnvelopeV1,
): Promise<void> {
  await request("PUT", `/api/models/${encodeURIComponent(modelId)}/ingestion`, jsonBody(envelope));
}

export async function getReadiness(modelId: string): Promise<ReadinessReport> {
  return guardReadiness(
    await requestJson("GET", `/api/models/${encodeURIComponent(modelId)}/readiness`),
  );
}

export async function getSpaces(modelId: string): Promise<ApiSpace[]> {
  return guardSpaces(await requestJson("GET", `/api/models/${encodeURIComponent(modelId)}/spaces`));
}

export async function patchSpace(
  modelId: string,
  spaceId: string,
  patch: SpacePatch,
): Promise<ApiSpace> {
  return guardSpace(
    await requestJson(
      "PATCH",
      `/api/models/${encodeURIComponent(modelId)}/spaces/${encodeURIComponent(spaceId)}`,
      jsonBody(patch),
    ),
  );
}

/**
 * One-click demo helper: classify unassigned occupancies (keyword-heuristic
 * first, honest `other` for the rest) and/or mark exits. Returns the updated
 * spaces + readiness.
 */
export async function autoAssignSpaces(
  modelId: string,
  options: AutoAssignOptions = {},
): Promise<AutoAssignResult> {
  return guardAutoAssign(
    await requestJson(
      "POST",
      `/api/models/${encodeURIComponent(modelId)}/spaces/auto-assign`,
      jsonBody(options),
    ),
  );
}

/** Storey rows with both server ids and IFC express ids (viewer isolation). */
export async function getModelStoreys(modelId: string): Promise<ApiStorey[]> {
  return guardStoreys(
    await requestJson("GET", `/api/models/${encodeURIComponent(modelId)}/storeys`),
  );
}

/** Fetch the stored Fragments binary for a model (3D viewer load-by-id). */
export async function getModelFragments(modelId: string): Promise<ArrayBuffer> {
  const res = await request("GET", `/api/models/${encodeURIComponent(modelId)}/fragments`);
  return res.arrayBuffer();
}

export async function getModelDiff(modelId: string): Promise<ModelDiff> {
  return guardDiff(await requestJson("GET", `/api/models/${encodeURIComponent(modelId)}/diff`));
}

// ---------------------------------------------------------------------------
// Wave 3 — MEP generation (pinned contract)
// ---------------------------------------------------------------------------

/**
 * Kick off procedural generation for a model. The server rolls a fresh seed
 * when `seed` is absent and answers 202 with the job to follow via SSE.
 */
export async function generateMepSystem(
  modelId: string,
  req: GenerateRequest,
): Promise<GenerateAccepted> {
  return guardGenerateAccepted(
    await requestJson("POST", `/api/models/${encodeURIComponent(modelId)}/generate`, jsonBody(req)),
  );
}

export async function listMepSystems(modelId: string): Promise<MepSystem[]> {
  return guardMepSystems(
    await requestJson("GET", `/api/models/${encodeURIComponent(modelId)}/mep-systems`),
  );
}

export async function getMepSystem(systemId: string): Promise<MepSystem> {
  return guardMepSystem(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}`),
  );
}

export async function getMepDevices(systemId: string): Promise<MepDevice[]> {
  return guardDevices(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/devices`),
  );
}

export async function getMepZones(systemId: string): Promise<MepZone[]> {
  return guardZones(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/zones`),
  );
}

export async function getMepCircuits(systemId: string): Promise<MepCircuit[]> {
  return guardCircuits(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/circuits`),
  );
}

export async function getMepFindings(systemId: string): Promise<ApiFinding[]> {
  return guardFindings(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/findings`),
  );
}

// ---------------------------------------------------------------------------
// Electrical (ENG-14935) — load calc + panel schedule (raw JSON for the
// prototype results view; boon-core owns the production UI, ENG-14936).
// ---------------------------------------------------------------------------

export async function getElectricalLoadCalc(systemId: string): Promise<unknown> {
  return requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/load-calc`);
}

export async function getElectricalPanelSchedule(systemId: string): Promise<unknown> {
  return requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/panel-schedule`);
}

/** Positioned electrical elements for the viewer overlay (receptacles +
 * luminaires + panels), in the shared LayerDevice shape. */
export interface ElectricalMarker {
  id: string;
  kind: string;
  storey_id: string;
  space_id: string;
  x_m: number;
  y_m: number;
  /** Mounting height above finished floor (metres). */
  z_m: number;
}
export async function getElectricalMarkers(systemId: string): Promise<ElectricalMarker[]> {
  const body = await requestJson(
    "GET",
    `/api/mep-systems/${encodeURIComponent(systemId)}/electrical-markers`,
  );
  const markers = (body as { markers?: unknown }).markers;
  return Array.isArray(markers) ? (markers as ElectricalMarker[]) : [];
}

/**
 * Raceway/conduit routing lives on the raw discipline Output (opt-in). Reads
 * only the conduit/pull-box/raceway-fitting arrays the viewer layer draws;
 * the server omits them from a non-conduit Output, so all three default to
 * empty. A 404 (ungenerated / legacy row with no persisted Output) surfaces
 * as no raceways rather than aborting the caller's load flow.
 */
export async function getElectricalOutput(systemId: string): Promise<ElectricalOutput> {
  const body = (await requestJson(
    "GET",
    `/api/mep-systems/${encodeURIComponent(systemId)}/output`,
  )) as ElectricalOutput | null;
  return {
    conduits: Array.isArray(body?.conduits) ? body.conduits : [],
    pull_boxes: Array.isArray(body?.pull_boxes) ? body.pull_boxes : [],
    raceway_fittings: Array.isArray(body?.raceway_fittings) ? body.raceway_fittings : [],
    grounding_conductors: Array.isArray(body?.grounding_conductors)
      ? body.grounding_conductors
      : [],
    grounding_electrodes: Array.isArray(body?.grounding_electrodes)
      ? body.grounding_electrodes
      : [],
    // Grounding schedule is `null`/absent when the grounding stage did not run.
    grounding_schedule: body?.grounding_schedule ?? null,
    // Emergency & standby power (NEC 517/700) — all `null`/empty when the
    // opt-in emergency stage did not run.
    emergency_source: body?.emergency_source ?? null,
    emergency_branch:
      body?.emergency_branch && typeof body.emergency_branch === "object"
        ? body.emergency_branch
        : {},
    ats: Array.isArray(body?.ats) ? body.ats : [],
    emergency_system: body?.emergency_system ?? null,
  };
}

/**
 * The raw mechanical `Output` (equipment + per-space load screening + BoQ),
 * read off the SAME generic `/output` endpoint `getElectricalOutput` uses. A
 * 404 (ungenerated / legacy row with no persisted Output) surfaces as empty
 * arrays rather than aborting the caller's load flow.
 */
export async function getMechanicalOutput(systemId: string): Promise<MechanicalOutput> {
  const body = (await requestJson(
    "GET",
    `/api/mep-systems/${encodeURIComponent(systemId)}/output`,
  )) as { equipment_schedule?: MechanicalOutput | null } | null;
  // Mechanical is HVAC expanded: the equipment schedule hangs off the HVAC
  // output under `equipment_schedule`, not at the top level. Absent (rather
  // than empty) when the server's equipment sub-stage degraded — the reason is
  // then carried as a finding, so degrade to the empty slice here.
  const schedule = body?.equipment_schedule ?? null;
  return {
    equipment: Array.isArray(schedule?.equipment) ? schedule.equipment : [],
    load_screening: Array.isArray(schedule?.load_screening) ? schedule.load_screening : [],
    boq: schedule?.boq ?? null,
  };
}

// ---------------------------------------------------------------------------
// Wave 4 — HVAC ductwork (pinned contract)
// ---------------------------------------------------------------------------

export async function getMepDucts(systemId: string): Promise<MepDuct[]> {
  return guardDucts(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/ducts`),
  );
}

export async function getMepFittings(systemId: string): Promise<MepFitting[]> {
  return guardFittings(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/fittings`),
  );
}

export async function getMepTerminals(systemId: string): Promise<MepTerminal[]> {
  return guardTerminals(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/terminals`),
  );
}

export async function getMepEquipment(systemId: string): Promise<MepEquipment[]> {
  return guardEquipment(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/equipment`),
  );
}

export async function getMepAirflows(systemId: string): Promise<MepAirflow[]> {
  return guardAirflows(
    await requestJson("GET", `/api/mep-systems/${encodeURIComponent(systemId)}/airflows`),
  );
}

/** Record an engineer override-with-reason on a finding. */
export async function overrideFinding(findingId: string, reason: string): Promise<void> {
  await request(
    "POST",
    `/api/findings/${encodeURIComponent(findingId)}/override`,
    jsonBody({ reason }),
  );
}

/** Remove a finding override. */
export async function clearFindingOverride(findingId: string): Promise<void> {
  await request("DELETE", `/api/findings/${encodeURIComponent(findingId)}/override`);
}

/** Download URL for the BoQ CSV of a generated system. */
export function boqCsvUrl(systemId: string): string {
  return `/api/mep-systems/${encodeURIComponent(systemId)}/boq.csv`;
}

// ---------------------------------------------------------------------------
// Wave 5 — deliverable exports (A5 contract)
// ---------------------------------------------------------------------------

/** Download URL for the riser-diagram SVG (fire-alarm systems only). */
export function riserSvgUrl(systemId: string): string {
  return `/api/mep-systems/${encodeURIComponent(systemId)}/riser.svg`;
}

/** Download URL for the PDF report (findings + overrides + confidence notes). */
export function reportPdfUrl(systemId: string): string {
  return `/api/mep-systems/${encodeURIComponent(systemId)}/report.pdf`;
}

/** Download URL for the DXF export of floor placements (model-level). */
export function placementsDxfUrl(modelId: string): string {
  return `/api/models/${encodeURIComponent(modelId)}/placements.dxf`;
}

/** Fetch the riser SVG markup as text (for the inline preview modal). */
export async function getRiserSvg(systemId: string): Promise<string> {
  const res = await request("GET", riserSvgUrl(systemId));
  return res.text();
}

// ---------------------------------------------------------------------------
// Wave 5 — codes monitor (server-canonical scraper contract; monitor-only,
// never writes rules — every detected change goes to the human review queue).
// Sources are list + create only; the run is global (all sources, one job).
// ---------------------------------------------------------------------------

export async function listScraperSources(): Promise<ScraperSource[]> {
  return guardScraperSources(await requestJson("GET", "/api/scraper/sources"));
}

export async function createScraperSource(create: ScraperSourceCreate): Promise<ScraperSource> {
  return guardScraperSource(await requestJson("POST", "/api/scraper/sources", jsonBody(create)));
}

/**
 * Kick off one change-detection run over ALL monitored sources; answers 202
 * with the job row — follow it via the SSE stream.
 */
export async function runScraperMonitors(): Promise<ScrapeRunAccepted> {
  return guardScrapeRun(await requestJson("POST", "/api/scraper/run"));
}

export async function listReviewItems(): Promise<ReviewItem[]> {
  return guardReviewItems(await requestJson("GET", "/api/scraper/review-items"));
}

/** Acknowledge/dismiss a detected change with reviewer notes. */
export async function patchReviewItem(itemId: string, patch: ReviewItemPatch): Promise<ReviewItem> {
  return guardReviewItem(
    await requestJson(
      "PATCH",
      `/api/scraper/review-items/${encodeURIComponent(itemId)}`,
      jsonBody(patch),
    ),
  );
}

/** The structural rows the member layer draws, read off the raw Output. */
export interface StructuralFrame {
  columns: LayerColumn[];
  beams: LayerBeam[];
  lateral: LayerLateral[];
}

/**
 * Structural frame for the viewer, read from the raw discipline Output.
 *
 * Deliberately goes through `/output` (the documented faithful substrate)
 * rather than adding per-element row endpoints for a prototype smoke-view. A
 * 404 or a non-structural system surfaces as an empty frame rather than
 * aborting the caller's load flow.
 *
 * # Shape translation
 * `/output` returns the ENGINE's serde shape, where plan points are TUPLES
 * (`position: [x, y]`, `start`/`end`, `min`/`max`) — not the flat `x_m`/
 * `start_x_m` columns the DB rows use. The layer wants flat fields, so the
 * translation happens here, at the boundary, the same way `conduitToLayer`
 * adapts raceways. Reading the tuple fields as if they were flat yields
 * `undefined` -> `NaN` coordinates and a silently empty frame, which is
 * exactly the bug this mapping exists to prevent.
 */
export async function getStructuralFrame(systemId: string): Promise<StructuralFrame> {
  const body = (await requestJson(
    "GET",
    `/api/mep-systems/${encodeURIComponent(systemId)}/output`,
  )) as Record<string, unknown> | null;

  const pt = (v: unknown): [number, number] | null =>
    Array.isArray(v) && v.length >= 2 && typeof v[0] === "number" && typeof v[1] === "number"
      ? [v[0], v[1]]
      : null;
  const rows = (key: string): Record<string, unknown>[] =>
    Array.isArray(body?.[key]) ? (body[key] as Record<string, unknown>[]) : [];

  const columns: LayerColumn[] = [];
  for (const c of rows("columns")) {
    const p = pt(c.position);
    if (!p) continue;
    columns.push({
      id: String(c.id ?? ""),
      grid_ref: String(c.grid_ref ?? ""),
      storey_id: String(c.storey_id ?? ""),
      level: Number(c.level ?? 0),
      x_m: p[0],
      y_m: p[1],
      size_m: Number(c.size_m ?? 0),
      base_z_m: Number(c.base_z_m ?? 0),
      height_m: Number(c.height_m ?? 0),
      designation: String(c.designation ?? ""),
    });
  }

  const beams: LayerBeam[] = [];
  for (const b of rows("beams")) {
    const start = pt(b.start);
    const end = pt(b.end);
    if (!start || !end) continue;
    beams.push({
      id: String(b.id ?? ""),
      role: b.role === "infill_beam" ? "infill_beam" : "girder",
      storey_id: String(b.storey_id ?? ""),
      level: Number(b.level ?? 0),
      start_x_m: start[0],
      start_y_m: start[1],
      end_x_m: end[0],
      end_y_m: end[1],
      z_m: Number(b.z_m ?? 0),
      span_m: Number(b.span_m ?? 0),
      designation: String(b.designation ?? ""),
    });
  }

  const lateral: LayerLateral[] = [];
  for (const l of rows("lateral")) {
    const min = pt(l.min);
    const max = pt(l.max);
    if (!min || !max) continue;
    lateral.push({
      id: String(l.id ?? ""),
      core_id: String(l.core_id ?? ""),
      kind: String(l.kind ?? "placeholder"),
      min_x_m: min[0],
      min_y_m: min[1],
      max_x_m: max[0],
      max_y_m: max[1],
      base_z_m: Number(l.base_z_m ?? 0),
      top_z_m: Number(l.top_z_m ?? 0),
    });
  }

  return { columns, beams, lateral };
}
